import React, { useState, useEffect } from "react";
import {
  FaLinkedin,
  FaWhatsapp,
  FaEnvelope,
  FaDownload,
  FaPlay,
} from "react-icons/fa";

export default function Home() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleScroll = () => setScrollY(window.scrollY);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="relative min-h-screen  bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 animate-pulse"></div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/10 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Dynamic Gradient Orb */}
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl transition-all duration-1000 ease-out"
          style={{
            left: mousePosition.x - 192,
            top: mousePosition.y - 192,
            transform: `translate(-50%, -50%) scale(${1 + scrollY * 0.001})`,
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-6 lg:px-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 text-sm font-medium text-white/80">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></div>
              Available for Adventures
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-5xl md:text-7xl font-black leading-tight">
                <span className="block text-white">Hello, I'm</span>
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-pulse">
                  Janardan Nepal
                </span>
              </h1>

              <div className="space-y-2">
                <p className="text-xl md:text-2xl text-gray-300 font-light">
                  Managing Director & Adventure Specialist
                </p>
                <div className="flex items-center justify-center lg:justify-start space-x-4 text-lg text-blue-400">
                  <span>Trekking Expert</span>
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span>Multilingual</span>
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span>Global Networker</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed">
              Transforming travel dreams into extraordinary adventures. With
              over 15 years of experience in the tourism industry, I specialize
              in creating unforgettable journeys through the majestic Himalayas.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/cv.pdf"; // Path relative to /public
                  link.download = "Janardan Nepal-CV.pdf"; // Suggested download filename
                  link.click();
                }}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold text-white overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25"
              >
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  <FaDownload className="w-5 h-5" />
                  <span>Download CV</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>

              <button className="group px-8 py-4 border-2 border-white/20 rounded-full font-semibold text-white backdrop-blur-xl hover:bg-white/10 transition-all duration-300">
                <span className="flex items-center justify-center space-x-2">
                  <FaPlay className="w-4 h-4" />
                  <span>Watch Story</span>
                </span>
              </button>
            </div>

            {/* Social Links */}
            <div className="flex items-center justify-center lg:justify-start space-x-6 pt-8">
              <div className="text-sm text-gray-400 font-medium">
                Follow me:
              </div>
              <div className="flex space-x-4">
                <a
                  href="https://linkedin.com"
                  className="group p-3 bg-white/10 backdrop-blur-xl rounded-full hover:bg-blue-600/20 transition-all duration-300 hover:scale-110"
                >
                  <FaLinkedin className="w-5 h-5 text-blue-400 group-hover:text-blue-300" />
                </a>
                <a
                  href="https://wa.me/9779841492029"
                  className="group p-3 bg-white/10 backdrop-blur-xl rounded-full hover:bg-green-600/20 transition-all duration-300 hover:scale-110"
                >
                  <FaWhatsapp className="w-5 h-5 text-green-400 group-hover:text-green-300" />
                </a>
                <a
                  href="mailto:info@topoftheworldadv.com"
                  className="group p-3 bg-white/10 backdrop-blur-xl rounded-full hover:bg-red-600/20 transition-all duration-300 hover:scale-110"
                >
                  <FaEnvelope className="w-5 h-5 text-red-400 group-hover:text-red-300" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Content - Profile Image */}
          <div className="relative  mb-12 flex justify-center lg:justify-end">
            <div className="relative group">
              {/* Main Image Container */}
              <div className="relative w-80 h-80 md:w-96 md:h-96">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl rotate-6 group-hover:rotate-3 transition-transform duration-500"></div>
                <div className="absolute inset-2 bg-slate-800 rounded-3xl overflow-hidden">
                  <img
                    src="https://media.topoftheworldadv.com/uploads/blog/author/janardan-nepal.webp"
                    alt="Janardan Nepal"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
                </div>

                {/* Floating Stats */}
                <div className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">15+</div>
                    <div className="text-sm text-black-300">
                      Years Experience
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">50+</div>
                    <div className="text-sm text-gray-300">
                      Countries Visited
                    </div>
                  </div>
                </div>
              </div>

              {/* Animated Rings */}
              <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 border-2 border-blue-400/20 rounded-full animate-ping"></div>
                <div
                  className="absolute inset-8 border-2 border-purple-400/20 rounded-full animate-ping"
                  style={{ animationDelay: "1s" }}
                ></div>
                <div
                  className="absolute inset-16 border-2 border-pink-400/20 rounded-full animate-ping"
                  style={{ animationDelay: "2s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      {/* <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center space-y-2 text-white/60 cursor-pointer hover:text-white/80 transition-colors">
          <span className="text-sm font-medium">Scroll to explore</span>
          <FaArrowDown className="w-4 h-4" />
        </div>
      </div> */}

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>
    </div>
  );
}
